import { TextField, InputAdornment, Button } from "@mui/material";
import React, { useState, useEffect } from "react";
import "../styles/password-recovery.scss";
import { IMaskInput } from 'react-imask';
import Modal from 'react-modal';
import { PasswordInput, SMSVerificationModal } from "../components";
import { updatePassword, userExists } from "../components/api";
import { setNotificationMessage, setShowNotification } from "../store";

const PhoneMask = React.forwardRef(function TextMaskCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
        <IMaskInput
            {...other}
            mask="(000) 000-00-00"
            definitions={{
                '#': /[1-9]/,
            }}
            inputRef={ref}
            onAccept={(value) => onChange({ target: { name: props.name, value } })}
            overwrite
        />
    );
});

export const PasswordRecovery = () => {

    Modal.setAppElement('#root');

    const [phone, setPhone] = useState(null);
    const [password, setPassword] = useState(null);
    const [confirmPassword, setConfirmPassword] = useState(null);
    const [numberConfirmed, setNumberConfirmed] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const phoneParam = urlParams.get('phone');
        if(phoneParam)
            setPhone(phoneParam.slice(2));
    }, []);

    const [errors, setErrors] = useState({
        phone: "",
        password: "",
        confirmPassword: "",
    });

    const formatPhone = (inputPhone) => {
        return `+7${inputPhone?.replace(/[^0-9+]/g, '')}`
    }

    const validatePhone = (phoneNumber) => {
        if (!phoneNumber?.trim()) {
            return "Номер телефона обязателен для заполнения";
        }

        const cleanPhone = phoneNumber.replace(/[()\s-]/g, '');

        if (cleanPhone.length !== 10) {
            return "Номер телефона должен содержать 10 цифр";
        }

        const phoneRegex = /^\d+$/;
        if (!phoneRegex.test(cleanPhone)) {
            return "Номер телефона должен содержать только цифры";
        }

        return "";
    };

    const validatePassword = (pass) => {
        if (!pass) {
            return "Пароль обязателен для заполнения";
        }

        if (pass.length < 8) {
            return "Пароль должен содержать минимум 8 символов";
        }

        return "";
    };

    const validateConfirmPassword = (confirmPass) => {
        if (password !== confirmPass) {
            return "Пароли не совпадают";
        }
        return "";
    };

    const validateField = (fieldName, value) => {
        let error = "";

        switch (fieldName) {
            case 'phone':
                error = validatePhone(value);
                break;
            case 'password':
                error = validatePassword(value);
                if (confirmPassword && value !== confirmPassword) {
                    setErrors(prev => ({
                        ...prev,
                        confirmPassword: "Пароли не совпадают"
                    }));
                }
                break;
            case 'confirmPassword':
                error = validateConfirmPassword(value);
                break;
            default:
                break;
        }
        setErrors(prev => ({
            ...prev,
            [fieldName]: error
        }));
    };

    const validateForm = () => {
        const newErrors = {
            phone: validatePhone(phone),
            password: validatePassword(password),
            confirmPassword: validateConfirmPassword(confirmPassword),
        };

        setErrors(newErrors);

        const hasErrors = Object.values(newErrors).some(error => error !== "");

        return !hasErrors;
    };

    const handlePhoneChange = (event) => {
        const value = event.target.value;
        setPhone(value);
        validateField('phone', value);
    };

    const handlePasswordChange = (event) => {
        const value = event.target.value;
        setPassword(value);
        validateField('password', value);
    };

    const handleConfirmPasswordChange = (event) => {
        const value = event.target.value;
        setConfirmPassword(value);
        validateField('confirmPassword', value);
    };

    useEffect(() => {
        validateForm();
    }, [phone, password, confirmPassword]);

    const continueClick = () => {
        userExists({phoneNumber: formatPhone(phone)}) 
            .then(() => { changePassword() })
            .catch(error => {
                const errorMessageData = error?.response?.data?.message;
                setNotificationMessage(errorMessageData
                    ? errorMessageData
                    : "Произошла непредвиденная ошибка");
                setShowNotification(true);
            });
    }

    const changePassword = () => {
        if(!numberConfirmed){
            setIsModalOpen(true);
        }
        else{
            updatePassword({ phoneNumber: formatPhone(phone), password: password})
                .then(() => { window.location.href = "/authorization";})
                .catch(error => {
                    const errorMessageData = error?.response?.data?.message;
                    setNotificationMessage(errorMessageData
                        ? errorMessageData
                        : "Произошла непредвиденная ошибка");
                    setShowNotification(true);
                });
        }
    }

    const handleConfirm = () => {
        setIsModalOpen(false);
        setNumberConfirmed(true);
    }

    return (
        <div className="password-recovery__main">
            <h2 className="password-recovery__header">
                {numberConfirmed ? "Придумайте новый пароль" : "Введите номер для подтверждения"}
            </h2>
            {!numberConfirmed && (
            <TextField
                value={phone ?? ""}
                onChange={handlePhoneChange}
                label="Номер телефона"
                className="password-recovery__input-field"
                error={!!errors.phone && phone !== null}
                helperText={phone !== null && errors.phone}
                slotProps={{
                    input: {
                        inputComponent: PhoneMask,
                        startAdornment: <InputAdornment position="start">+7</InputAdornment>
                    },
                    htmlInput: {
                        inputMode: 'numeric'
                    }
                }}
                required
            />)} 
            
            {numberConfirmed && (
                <>
                    <PasswordInput
                        label="Пароль"
                        className="password-recovery__input-field"
                        value={password ?? ""}
                        onChange={handlePasswordChange}
                        error={!!errors.password && password !== null}
                        helperText={password !== null && errors.password}
                        required
                        fullWidth
                        margin="normal"
                    />
                    <PasswordInput
                        label="Подтверждение пароля"
                        className="password-recovery__input-field"
                        value={confirmPassword ?? ""}
                        onChange={handleConfirmPasswordChange}
                        error={!!errors.confirmPassword && confirmPassword !== null}
                        helperText={confirmPassword !== null && errors.confirmPassword}
                        required
                        fullWidth
                        margin="normal"
                    />
                </>
            )}
            <Button className="password-recovery__enter" variant="contained" disabled={!!errors.phone || phone == null} onClick={() => continueClick()}>
                {!numberConfirmed ? "Отправить код" : "Сохранить"}
            </Button>
            
            <SMSVerificationModal 
                isOpen={isModalOpen}
                phone={formatPhone(phone)}
                onClose={() => setIsModalOpen(false)}
                onConfirm={handleConfirm}
            />

        </div>
    )
}